import { PlayerSide } from '../types/game.types';
import type { Difficulty, MoveResult, GridPosition, PieceData } from '../types/game.types';
import type { Board } from '../entities/Board';
import type { MoveSystem } from './MoveSystem';

const BOARD_SIZE = 7;
const CENTER = Math.floor(BOARD_SIZE / 2);

const DEPTH_BY_DIFFICULTY: Record<Difficulty, number> = {
  easy: 0,
  medium: 2,
  hard: 4,
};

export class AISystem {
  private difficulty: Difficulty;

  constructor(difficulty: Difficulty = 'medium') {
    this.difficulty = difficulty;
  }

  async takeTurn(board: Board, moveSystem: MoveSystem): Promise<MoveResult | null> {
    await this.delay(500);

    const coconuts = board.getPiecesBySide(PlayerSide.COCONUT);
    const piecesWithMoves = coconuts.filter((piece) => {
      const moves = moveSystem.getAllValidMoves(board, piece.position);
      return moves.length > 0;
    });

    if (piecesWithMoves.length === 0) return null;

    if (this.difficulty === 'easy') {
      return this.randomMove(board, moveSystem, piecesWithMoves);
    }

    return this.bestMove(board, moveSystem, DEPTH_BY_DIFFICULTY[this.difficulty]);
  }

  private randomMove(
    board: Board,
    moveSystem: MoveSystem,
    piecesWithMoves: PieceData[],
  ): MoveResult {
    const piece = piecesWithMoves[Math.floor(Math.random() * piecesWithMoves.length)];
    const { moveTiles, attackTiles } = moveSystem.calculateValidMoves(board, piece.position);
    const targets: GridPosition[] = attackTiles.length > 0 ? attackTiles : moveTiles;
    const target = targets[Math.floor(Math.random() * targets.length)];

    const capturedPiece = board.getPiece(target);
    const from: GridPosition = { ...piece.position };

    board.removePiece(from);
    if (capturedPiece !== null) board.removePiece(target);
    const movedPiece: PieceData = { ...piece, position: { ...target } };
    board.setPiece(target, movedPiece);

    return { fromPosition: from, toPosition: target, capturedPiece, movedPiece };
  }

  private bestMove(board: Board, moveSystem: MoveSystem, depth: number): MoveResult | null {
    const coconuts = board.getPiecesBySide(PlayerSide.COCONUT);

    let bestScore = -Infinity;
    let bestFrom: GridPosition | null = null;
    let bestTo: GridPosition | null = null;

    for (const piece of coconuts) {
      const moves = moveSystem.getAllValidMoves(board, piece.position);
      for (const to of moves) {
        const sim = board.clone();
        const captured = sim.getPiece(to);
        const from = { ...piece.position };
        sim.removePiece(from);
        if (captured !== null) sim.removePiece(to);
        sim.setPiece(to, { ...piece, position: { ...to } });

        const score = this.minimax(sim, moveSystem, depth - 1, false, -Infinity, Infinity);
        if (score > bestScore) {
          bestScore = score;
          bestFrom = from;
          bestTo = to;
        }
      }
    }

    if (bestFrom === null || bestTo === null) return null;

    const piece = board.getPiece(bestFrom)!;
    const capturedPiece = board.getPiece(bestTo);
    board.removePiece(bestFrom);
    if (capturedPiece !== null) board.removePiece(bestTo);
    const movedPiece: PieceData = { ...piece, position: { ...bestTo } };
    board.setPiece(bestTo, movedPiece);

    return {
      fromPosition: bestFrom,
      toPosition: bestTo,
      capturedPiece,
      movedPiece,
    };
  }

  minimax(
    board: Board,
    moveSystem: MoveSystem,
    depth: number,
    isMaximising: boolean,
    alpha: number,
    beta: number,
  ): number {
    const coconuts = board.getPiecesBySide(PlayerSide.COCONUT);
    const kiwis = board.getPiecesBySide(PlayerSide.KIWI);

    if (coconuts.length === 0) return -10000;
    if (kiwis.length === 0) return 10000;
    if (depth === 0) return this.evaluateBoard(board);

    const pieces = isMaximising ? coconuts : kiwis;

    if (isMaximising) {
      let maxEval = -Infinity;
      outer: for (const piece of pieces) {
        const moves = moveSystem.getAllValidMoves(board, piece.position);
        for (const to of moves) {
          const sim = board.clone();
          const captured = sim.getPiece(to);
          sim.removePiece(piece.position);
          if (captured !== null) sim.removePiece(to);
          sim.setPiece(to, { ...piece, position: { ...to } });

          const score = this.minimax(sim, moveSystem, depth - 1, false, alpha, beta);
          if (score > maxEval) maxEval = score;
          if (score > alpha) alpha = score;
          if (beta <= alpha) break outer;
        }
      }
      return maxEval === -Infinity ? this.evaluateBoard(board) : maxEval;
    } else {
      let minEval = Infinity;
      outer: for (const piece of pieces) {
        const moves = moveSystem.getAllValidMoves(board, piece.position);
        for (const to of moves) {
          const sim = board.clone();
          const captured = sim.getPiece(to);
          sim.removePiece(piece.position);
          if (captured !== null) sim.removePiece(to);
          sim.setPiece(to, { ...piece, position: { ...to } });

          const score = this.minimax(sim, moveSystem, depth - 1, true, alpha, beta);
          if (score < minEval) minEval = score;
          if (score < beta) beta = score;
          if (beta <= alpha) break outer;
        }
      }
      return minEval === Infinity ? this.evaluateBoard(board) : minEval;
    }
  }

  evaluateBoard(board: Board): number {
    const coconuts = board.getPiecesBySide(PlayerSide.COCONUT);
    const kiwis = board.getPiecesBySide(PlayerSide.KIWI);

    // Piece count differential (positive = good for coconuts)
    let score = (coconuts.length - kiwis.length) * 100;

    // Advancement: coconuts start at rows 5-6, advance toward row 0
    for (const piece of coconuts) {
      score += (BOARD_SIZE - 1 - piece.position.row) * 3;
      score += (CENTER - Math.abs(piece.position.col - CENTER));
    }

    // Penalise kiwi advancement toward row 6
    for (const piece of kiwis) {
      score -= piece.position.row * 3;
      score -= (CENTER - Math.abs(piece.position.col - CENTER));
    }

    return score;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
